<script setup lang="ts">
import {
  WidgetDiscussionsSelectCategory,
  type IDiscussionCategory
} from '~/components/widget/discussions'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import type { IDiscussionItem } from '~/components/entity/discussion/item'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { ContentBlock } from '~/components/ui/content-block'
import { WidgetComments } from '~/components/widget/comments'

const { t } = useI18n()
const route = useRoute()

const questionUrl = `/questions/${route.params.question}`
const { data } = await useApiWith404<IResponseData<IDiscussionItem>>(() => questionUrl, {
  key: `question-${route.params.question}`,
  watch: [() => route.params.question],
  immediate: true
})

const question = computed(() => data.value?.data)

const url = computed(() => useUrlWithParams('/question_categories', {}))
const { data: categoriesData } = await useApi<IResponseData<IDiscussionCategory[]>>(() => url, {
  key: 'discussions-select-category-widget'
})

const categories = computed(() => categoriesData.value?.data || [])

const breadcrumbs = computed(() => [
  { title: t('page.discussions.view_page.breadcrumbs_discussions'), url: '/discussions' },
  {
    title: question.value?.title || t('page.discussions.view_page.breadcrumbs_fallback'),
    url: question.value?.url
  }
])

// Questions and answers are written by users in one language and are not translated: the
// Kyrgyz page shows the same text, so the Russian page is the only canonical one and
// there are no language versions to link (the backend sitemap lists it the same way)
const { disableGenericAlternateLinks } = useAlternateLinks()
disableGenericAlternateLinks()

const baseUrl = useRuntimeConfig().public.siteUrl as string
const russianPath = computed(() => question.value?.url || route.path.replace(/^\/ky(?=\/)/, ''))

useHead(() => ({
  title: question.value?.title
    ? t('page.discussions.view_page.meta_title_with_question', { question: question.value.title })
    : t('page.discussions.view_page.meta_title_fallback'),
  link: [{ key: 'canonical', rel: 'canonical', href: baseUrl + encodeURI(russianPath.value) }]
}))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <Button as-child>
              <NuxtLinkLocale class="w-full" to="/discussions/create">{{
                $t('page.discussions.category_page.ask_question_button')
              }}</NuxtLinkLocale>
            </Button>
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetDiscussionsSelectCategory :categories="categories" />
          </aside>
        </template>
        <div class="space-y-5">
          <ContentBlock variant="blue" heading-tag="h1" :heading-text="question?.title">
            <p v-hydrated-html="question?.description" />
          </ContentBlock>
          <WidgetComments
            v-if="question"
            commentable-type="question"
            :commentable-id="question.id"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
